/**
 * Cookie consent state for non-essential (analytics) cookies.
 *
 * The user's choice is persisted in localStorage under a single key. Until a
 * choice is recorded the value is `null`, which the banner treats as "ask" and
 * the analytics layer treats as "do not capture" (consent must be explicit).
 */

export type ConsentChoice = 'accepted' | 'declined';

export const CONSENT_STORAGE_KEY = 'otalytics_cookie_consent';

/**
 * Session replay ("screen recording") consent, stored and reasoned about
 * SEPARATELY from analytics consent. Accepting analytics cookies does not
 * enable recording: being filmed is a distinct ask. Until the user explicitly
 * opts in, replay stays OFF (`null` / `declined` both mean "do not record").
 */
export type ReplayConsentChoice = 'accepted' | 'declined';

export const REPLAY_CONSENT_STORAGE_KEY = 'otalytics_replay_consent';

/**
 * Custom event dispatched on `window` whenever the stored choice changes, so
 * that mounted consumers (the PostHog provider) can react without polling.
 */
export const CONSENT_CHANGED_EVENT = 'otalytics:cookie-consent-changed';

/**
 * Custom event dispatched on `window` whenever the stored replay choice
 * changes, mirroring {@link CONSENT_CHANGED_EVENT} for analytics consent.
 */
export const REPLAY_CONSENT_CHANGED_EVENT = 'otalytics:replay-consent-changed';

/**
 * Read the persisted choice. Returns `null` when nothing has been recorded yet
 * or when storage is unavailable (e.g. SSR, private mode throwing).
 */
export function getConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Persist the choice and notify listeners. Storage failures are swallowed so a
 * blocked localStorage never breaks the UI; the event still fires so analytics
 * gating reflects the in-session decision.
 */
export function setConsent(choice: ConsentChoice): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // ignore – event below still lets the session honour the choice
  }

  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: choice }));
}

/**
 * Read the persisted replay choice. Returns `null` when nothing is recorded
 * yet or storage is unavailable. Replay is opt-in, so both `null` and
 * `declined` mean "do not record".
 */
export function getReplayConsent(): ReplayConsentChoice | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.localStorage.getItem(REPLAY_CONSENT_STORAGE_KEY);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Persist the replay choice and notify listeners, mirroring
 * {@link setConsent}. Storage failures are swallowed; the event still fires so
 * the in-session decision is honoured.
 */
export function setReplayConsent(choice: ReplayConsentChoice): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(REPLAY_CONSENT_STORAGE_KEY, choice);
  } catch {
    // ignore – event below still lets the session honour the choice
  }

  window.dispatchEvent(
    new CustomEvent(REPLAY_CONSENT_CHANGED_EVENT, { detail: choice }),
  );
}
